// PhotoGroup (job page round): one visit's photos in one row. Its stages (before, during, after, or the
// app's own categories) sit side by side, each with its count, so one visit's before sits right above
// the next visit's after and the two compare at a glance (job-page-rules §9: Photos and forms). A stage
// with no photos isn't drawn; a visit with none says so in one muted line. A stage with more than
// `limit` ends in "+n", which opens the viewer at the first one not shown. Captions are on hover and in
// the viewer. PhotoStrip is the closed line's strip: the first few, small.
// Reads only --photos-*; the thumbnails read their own.
import type { ReactNode } from 'react';
import { MoreThumb, PhotoThumb, type Photo } from '../PhotoThumb/PhotoThumb.tsx';
import './PhotoGroup.css';

export interface PhotoStage {
  id: string;
  /** Its name, lower case as it reads in the row ("before"). */
  label: string;
  photos: Photo[];
}

export interface PhotoGroupProps {
  /** Whose photos: "Visit 1". */
  title: string;
  /** Under it: the day ("Wed Oct 21"). */
  sub?: string;
  stages: PhotoStage[];
  /** A thumbnail was pressed: open the viewer at that photo. */
  onOpen?: (photoId: string) => void;
  /** The most thumbnails a stage draws, its "+n" among them (7: one line in the main column). */
  limit?: number;
  /** What a visit with no photos says. */
  emptyText?: string;
  /** Round 2 (point 10): the visit's forms (RecordRows) under its photos, in the same column. */
  forms?: ReactNode;
  /** Round 2 (point 10): the visit's name as a line at the section's edge, its photos and forms under
   * it on the record's one grid (no label column). */
  isStacked?: boolean;
}

export function PhotoGroup({
  title,
  sub,
  stages,
  onOpen,
  limit = 7,
  emptyText = 'No photos from this visit',
  forms,
  isStacked = false,
}: PhotoGroupProps) {
  const shown = stages.filter((s) => s.photos.length > 0);
  return (
    <section
      className="fl-photos"
      data-stacked={isStacked ? '' : undefined}
      aria-label={sub ? `${title}, ${sub}` : title}
    >
      <div className="fl-photos__head" aria-hidden="true">
        <span className="fl-photos__title">{title}</span>
        {sub ? <span className="fl-photos__sub">{sub}</span> : null}
      </div>
      {shown.length === 0 ? (
        <p className="fl-photos__empty">{emptyText}</p>
      ) : (
        <div className="fl-photos__stages">
          {shown.map((stage) => {
            const n = stage.photos.length;
            const cut = n > limit ? limit - 1 : n;
            const rest = stage.photos[cut];
            return (
              <div className="fl-photos__stage" key={stage.id}>
                <h3 className="fl-photos__label">
                  {stage.label} · {n}
                </h3>
                <div className="fl-photos__row">
                  {stage.photos.slice(0, cut).map((p) => (
                    <PhotoThumb key={p.id} photo={p} onPress={onOpen} />
                  ))}
                  {rest ? <MoreThumb count={n - cut} onPress={() => onOpen?.(rest.id)} /> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {forms ? <div className="fl-photos__forms">{forms}</div> : null}
    </section>
  );
}

export interface PhotoStripProps {
  photos: Photo[];
  /** How many it draws (5). */
  max?: number;
  onOpen?: (photoId: string) => void;
}

/** A closed line's strip: the first few photos, small; each opens the viewer in one press. */
export function PhotoStrip({ photos, max = 5, onOpen }: PhotoStripProps) {
  return (
    <span className="fl-photos__strip">
      {photos.slice(0, max).map((p) => (
        <PhotoThumb key={p.id} photo={p} size="sm" onPress={onOpen} />
      ))}
    </span>
  );
}
